import type { ReactElement } from "react";
import { cloneElement } from "react";

import type { Dayjs } from "dayjs";
import { upperFirst } from "lodash";

import { shortMonthDayAndYear } from "@similarweb/pro-logic-components";
import Sentry from "@similarweb/sentry-logger";

import { getCountryDropdownButtonTextAndIcon } from "app/components/campaigns/queryBar-report/reports-pages-filters/CamapaignSelectedCountryItem";
import type {
    ICountryDropdownItemProps,
    ILocationObject,
} from "app/components/campaigns/queryBar-report/reports-pages-filters/CmapaignsIfraTypes";
import {
    CAMPAIGNS_ALL_TIME_ID,
    CAMPAIGNS_DURATION_ONE_WEEK_ID,
    CAMPAIGNS_MONTHS_RANGE_INTERVALS,
    FIRST_DAY_CREATION_ID,
} from "app/components/campaigns/queryBar-report/reports-pages-filters/durationPicker/CampaignDurationPicker.consts";
import {
    getAllTimeDateRangeForCampaign,
    getDisplayTextForRelativePickedDurationForCampaigns,
    getTimeDiffBetweenDatesUTC,
} from "app/components/campaigns/queryBar-report/reports-pages-filters/durationPicker/CampaignDurationPickerUtils";
import { getAvailableLocations } from "app/components/campaigns/queryBar-report/reports-pages-filters/locationPicker/CampaignsLocationFilterUtils";
import type { ICampaignsScrapingConfiguration } from "app/components/campaigns/shared/CampaignsConst";
import {
    getLocaleForScrapingConfig,
    getScarpingConfigsForCurrentDevice,
} from "app/components/campaigns/shared/CampaignsUtils";
import type { ISubtitleTextItem } from "app/components/widget/widget-types/Widget";
import { translate } from "app/filters/i18nFilter";
import { RANK_TRACKER_CAMPAIGNS_CONFIG_KEY } from "app/common/campaigns/KeywordsCampaignConsts";
import {
    getCampaignMaxAvailableDate,
    getCampaignsFromServer,
    getRTDurationObjectForCampaign,
    getRtCampaignDataStartDate,
    withCampaignDatesUtc,
} from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignsContextUtils";
import type {
    IKeywordsCampaignData,
    IRTCurrentCampaignData,
    RtCampaignScrapeContext,
} from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import { granularityModulus } from "app/components/campaigns/shared/overview/constants";
import type { DatesRange } from "app/pages/sales-intelligence/sub-modules/account-overview/shared/types/common";

import { EKWReportTableFilters, KWReportWidgetFiltersTooltipMap, TAGS_DELIMITER } from "./campaigns.consts";

export { getCampaignMaxAvailableDate, getRTDurationObjectForCampaign, getRtCampaignDataStartDate };

export const getParsedDataFromLocalStorage = <T,>(key: string): T | undefined => {
    let parsedData: T;
    try {
        const data = localStorage.getItem(key);
        parsedData = JSON.parse(data);
    } catch (error) {
        return undefined;
    }
    return parsedData;
};

export const getAllCampaigns = async () => {
    let parsedCampaigns = getParsedDataFromLocalStorage<IKeywordsCampaignData[]>(RANK_TRACKER_CAMPAIGNS_CONFIG_KEY);
    if (!parsedCampaigns) {
        parsedCampaigns = await getCampaignsFromServer();
    }
    return mapCampaignsWithDatesUtc(parsedCampaigns);
};

export const getCampaignsFromLocalStorage = () => {
    const parsedCampaigns = getParsedDataFromLocalStorage<IKeywordsCampaignData[]>(RANK_TRACKER_CAMPAIGNS_CONFIG_KEY);
    return mapCampaignsWithDatesUtc(parsedCampaigns);
};

const mapCampaignsWithDatesUtc = (campaigns: IKeywordsCampaignData[]) => {
    return campaigns?.map((campaign) => {
        try {
            return withCampaignDatesUtc(campaign);
        } catch (error) {
            Sentry.error(
                `Error parsing campaigns configurations from local storage in rank tracker dashboards-`,
                error,
            );
        }
    });
};

export const getSelectedLocationsForURL = (
    selectedLocations: string,
    scrapingConfigurations: ICampaignsScrapingConfiguration[],
): string => {
    return selectedLocations
        ?.toString()
        .split(",")
        .map((configId) => {
            const scrapingConfig = scrapingConfigurations?.find((scrapingConf) => configId === scrapingConf.id);
            if (!scrapingConfig) {
                return null;
            }
            return getLocaleForScrapingConfig(scrapingConfig);
        })
        .join("&");
};

const getDurationForURL = (campaignDataStart: Dayjs, originalDuration: string, maxDate?: string): string => {
    if (originalDuration === CAMPAIGNS_ALL_TIME_ID && campaignDataStart) {
        const durationObject = getAllTimeDateRangeForCampaign(campaignDataStart, maxDate);
        const amountOFDays = getTimeDiffBetweenDatesUTC(durationObject);
        if (amountOFDays !== undefined) {
            return `${amountOFDays}d`;
        }
    }
    return originalDuration;
};

export const getDurationForURLWithCampaignMax = (
    originalDuration: string,
    campaign: RtCampaignScrapeContext | null | undefined,
): string => {
    const campaignDataStart = getRtCampaignDataStartDate(campaign);
    if (!campaignDataStart) {
        return originalDuration === CAMPAIGNS_ALL_TIME_ID ? FIRST_DAY_CREATION_ID : originalDuration;
    }
    return getDurationForURL(
        campaignDataStart,
        originalDuration,
        getCampaignMaxAvailableDate(campaign?.lastScrapeDate),
    );
};

const getSelectedLocationsFromSelectedScrapingConfigs = (
    selectedScrapingConfs: string,
    availableLocations: ILocationObject[],
    currentCampaign: IRTCurrentCampaignData,
    device: string,
): ILocationObject[] => {
    const selectedScrapingConfsList = selectedScrapingConfs?.toString()?.split(",");
    const scarpingConfigsForCurrentDevice: ICampaignsScrapingConfiguration[] = getScarpingConfigsForCurrentDevice(
        currentCampaign?.scrapingConfigurations,
        device,
    );

    const selectedCountries = availableLocations?.filter((currentLocation) => {
        const availableLocationWithoutLocale = currentLocation.id.split("_")[0];
        return selectedScrapingConfsList?.some((location) => {
            const currentScrapingConf: ICampaignsScrapingConfiguration = scarpingConfigsForCurrentDevice.find(
                (sc) => sc.id === location,
            ); //geoLocation
            return currentScrapingConf?.geoLocation === availableLocationWithoutLocale;
        });
    });
    return selectedCountries;
};

export const getUpdateLocationsOnViewData = (parsedLanguages, properties): ICountryDropdownItemProps => {
    const { webSource, currentCampaignConfiguration, country: countryProp } = properties;
    const availableLocations: ILocationObject[] = getAvailableLocations(
        currentCampaignConfiguration?.scrapingConfigurations,
        webSource,
        parsedLanguages,
    );

    const selectedCountries = getSelectedLocationsFromSelectedScrapingConfigs(
        countryProp,
        availableLocations,
        currentCampaignConfiguration,
        webSource,
    );

    const country = getCountryDropdownButtonTextAndIcon(
        selectedCountries,
        webSource,
        currentCampaignConfiguration?.scrapingConfigurations,
    );

    return country;
};

const getRelativeDurationText = (durationObject: DatesRange, isLastWeekSelected, duration: string) => {
    if (isLastWeekSelected) {
        return translate("datepicker.lastn", {
            count: "7",
            unit: translate("datepicker.units.day.nomerous"),
        });
    } else {
        return getDisplayTextForRelativePickedDurationForCampaigns(durationObject, Number(duration.at(0)));
    }
};

export const getDataAccordingToGrarnularity = (data, granularity: string) => {
    const modulus = granularityModulus[granularity] ?? granularityModulus.Daily;
    const reversedData = [...data].reverse();
    const filteredData = reversedData.filter((_, index) => {
        const indexFromEnd = index + 1;
        return indexFromEnd % modulus === 0;
    });

    return filteredData.reverse();
};

const getDurationText = (duration, durationObject) => {
    const isLastWeekSelected = CAMPAIGNS_DURATION_ONE_WEEK_ID === duration;
    const isRelativeMonthIntervalSelected = CAMPAIGNS_MONTHS_RANGE_INTERVALS.map((interval) => `${interval}m`).some(
        (intrvalToCheck) => intrvalToCheck === duration,
    );
    const isRelativeDuration = isLastWeekSelected || isRelativeMonthIntervalSelected;
    if (isRelativeDuration && duration) {
        return getRelativeDurationText(durationObject, isLastWeekSelected, duration);
    }

    return `${durationObject?.from?.format(shortMonthDayAndYear)}-${durationObject?.to?.format(shortMonthDayAndYear)}`;
};

export const getDurationToPresent = (duration: string, durationObject: DatesRange) => {
    if (duration === CAMPAIGNS_ALL_TIME_ID) {
        return translate("datepicker.allTime");
    }
    return getDurationText(duration, durationObject);
};

export const getFiltersTooltip = (params): ISubtitleTextItem => {
    const filtersToValidate = [
        EKWReportTableFilters.POSITIONS,
        EKWReportTableFilters.CPC,
        EKWReportTableFilters.SERP_FILTER_TYPES,
        EKWReportTableFilters.KWDIFF,
        EKWReportTableFilters.VOLUME,
        EKWReportTableFilters.INTENT,
        EKWReportTableFilters.TAGS,
        EKWReportTableFilters.INCLUDE_KEYWORDS,
        EKWReportTableFilters.EXCLUDE_KEYWORDS,
    ];
    const mappedFilters: string[] = filtersToValidate.map((filter) =>
        KWReportWidgetFiltersTooltipMap.get(filter)(params),
    );
    const availableFilters: string[] = mappedFilters.filter((toFilter) => toFilter);
    const amountOfFilters = availableFilters.length;
    if (!amountOfFilters) {
        return;
    }

    const filtersTextSubtitle =
        amountOfFilters > 1
            ? translate("dashboard.widget.subtitle.rankTracker.filters.multiple", {
                  amount: amountOfFilters,
              })
            : availableFilters[0];

    const tooltipValue = availableFilters.map((filter) => <div key={filter}>{filter}</div>);
    return {
        text: filtersTextSubtitle,
        tooltipValue,
    };
};

const isTextPartsGreaterThanOne = (text: string | string[]): boolean => {
    if (Array.isArray(text)) {
        return text.length > 1;
    }
    return text.split(TAGS_DELIMITER)?.length > 1;
};

const transformTagsText = (text = ""): string => {
    const textParts = text.split(TAGS_DELIMITER);
    if (isTextPartsGreaterThanOne(textParts)) {
        return translate("dashboard.widget.subtitle.rankTracker.tags.multiple", {
            count: textParts?.length,
        });
    }

    return upperFirst(text);
};

const addBlankSpacesToText = (text: string) => text.replace(/,/g, ", ");

const transformTagsTooltipText = (element: ReactElement<{ children: string }>): ReactElement => {
    let tooltipText = upperFirst(element.props.children).toString();
    if (isTextPartsGreaterThanOne(tooltipText)) {
        tooltipText = addBlankSpacesToText(tooltipText);
    }

    return {
        ...element,
        props: { ...element.props, children: tooltipText },
    };
};

export const transformAndCapitalizeTexts = (subtitleTextItem: ISubtitleTextItem): ISubtitleTextItem => {
    const transformedTagsText = transformTagsText(subtitleTextItem?.text as string);
    const transformedTagsTooltipText = transformTagsTooltipText(cloneElement(subtitleTextItem.tooltipValue?.[0]));
    return {
        tooltipValue: transformedTagsTooltipText,
        text: transformedTagsText,
    };
};
